import { type RefObject, useState } from "react";

import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router";

import {
  MessageCircleIcon,
  MessagesSquareIcon,
  PaperclipIcon,
} from "lucide-react";

import type { OpenAPI } from "simplydo/interfaces";
import { useTheme } from "simplydo/react/theme";
import { Button } from "simplydo/react/ui/button";
import { Divider } from "simplydo/react/ui/divider";
import { Dropdown } from "simplydo/react/ui/dropdown";
import { H5 } from "simplydo/react/ui/header";
import { Modal, ModalActions, ModalContent } from "simplydo/react/ui/modal";
import {
  Placeholder,
  PlaceholderLine,
  PlaceholderParagraph,
} from "simplydo/react/ui/placeholder";

import { useAppSelector } from "store";
import util from "utils/utils";

import Questions from "components/challenges/Challenge/Questions/Questions";
import type { ChallengeDetail } from "components/challenges/types";
import { useAssessmentEditor } from "components/ideas/hooks/useAssessmentEditor";
import { ENTITY_TOOLBAR_HEIGHT } from "components/lib/EntityHeader";
import ReportContentChip from "components/lib/ReportContent/ReportContentChip";
import { SideNav } from "components/lib/SideNav";
import { NAV_BAR_HEIGHT } from "components/lib/UI";

import IdeaTags from "../Tags";
import IdeaTeam, { RequestIdeaTeam } from "../Team";
import { useIdea } from "../hooks/useIdea";
import type { UpdateIdeaFn } from "../types";
import useScrollTracking, { type ItemRefs } from "../useIdeaScrollTracking";
import { createAssessmentSectionEntry } from "./AssessmentSectionEntry";
import { createCustomSectionEntry } from "./CustomSectionEntry";
import { createImpactSectionEntry } from "./ImpactSectionEntry";

type IdeaSideBarProps = {
  isEditing?: boolean;
  sectionRefs?: RefObject<ItemRefs>;
  fieldRefs?: RefObject<ItemRefs>;
  scrollRef?: RefObject<HTMLDivElement | null>;
  updateIdea?: UpdateIdeaFn;
  leaveIdea?: () => void;
  main?: any;
};

const IdeaSidebar = ({
  isEditing,
  sectionRefs,
  fieldRefs,
  updateIdea,
  leaveIdea,
  main,
  scrollRef,
}: IdeaSideBarProps) => {
  const [idea, ideaQuery] = useIdea();
  const loading = ideaQuery.isLoading;
  const { t } = useTranslation();
  const [isShowingQuestions, setIsShowingQuestions] = useState(false);
  const user = useAppSelector((state) => state.user);
  const [searchParams, setSearchParams] = useSearchParams();

  const theme = useTheme();
  const { activeItemId, scrollToItem } = useScrollTracking(
    sectionRefs,
    scrollRef,
    -260,
  );
  const fieldScroll = useScrollTracking(fieldRefs, scrollRef, -300);

  const {
    isAssessingSelectedRound,
    selectedAssessmentRoundId,
    roundsToAssess,
    assessmentState,
  } = useAssessmentEditor(idea);

  const { ownerIdeaTemplate } = idea ?? {};
  const { body = [], assessments } = ownerIdeaTemplate ?? {};

  const canEdit = util.canEditIdea(user, idea);
  const noopUpdate: UpdateIdeaFn = () => {};
  const noopLeave = () => {};
  const safeUpdateIdea = updateIdea ?? noopUpdate;
  const safeLeaveIdea = leaveIdea ?? noopLeave;
  const canManageChallenge = util.canManageChallenge(
    user,
    idea?.ownerChallenge,
  );
  return (
    <>
      <Modal
        open={isShowingQuestions}
        onClose={() => setIsShowingQuestions(false)}
      >
        <ModalContent scrolling>
          {idea?.ownerChallenge ? (
            <Questions
              challenge={idea.ownerChallenge as ChallengeDetail}
              nonAdminView
            />
          ) : null}
        </ModalContent>
        <ModalActions>
          <Button onClick={() => setIsShowingQuestions(false)}>Close</Button>
        </ModalActions>
      </Modal>
      <SideNav
        top={NAV_BAR_HEIGHT + ENTITY_TOOLBAR_HEIGHT}
        disableScrollToTop
        beforeContent={
          <>
            {roundsToAssess.length > 1 ? (
              <div>
                <H5>Select assessment</H5>
                <Dropdown
                  variant="outline"
                  fluid
                  placeholder="Select assessment"
                  value={selectedAssessmentRoundId ?? undefined}
                  onChange={(e, { value }) => {
                    searchParams.set("assessmentRound", value as string);
                    setSearchParams(searchParams);
                  }}
                  options={assessments?.rounds
                    ?.filter((round) => roundsToAssess.includes(round.id))
                    .map((round) => ({
                      key: round.id,
                      text: round.name,
                      value: round.id,
                    }))}
                />
              </div>
            ) : null}
            {(theme.sizes.isComputer || !main) && loading ? (
              <Placeholder style={{ marginBottom: 12 }}>
                <PlaceholderLine style={{ height: 18, width: "60%" }} />
              </Placeholder>
            ) : theme.sizes.isComputer || !main ? (
              <H5>
                {t("common:capitalise", { key: "generic.idea" })} sections
              </H5>
            ) : null}
          </>
        }
        navItems={[
          loading
            ? {
                key: "loading",
                type: "custom" as const,
                content: (
                  <Placeholder>
                    <PlaceholderParagraph gap={0.6}>
                      <PlaceholderLine style={{ height: 28, width: "80%" }} />
                      <PlaceholderLine style={{ height: 28, width: "65%" }} />
                      <PlaceholderLine style={{ height: 28, width: "75%" }} />
                      <PlaceholderLine style={{ height: 28, width: "55%" }} />
                    </PlaceholderParagraph>
                  </Placeholder>
                ),
              }
            : null,
          ...body.flatMap((section) => {
            if (section.type === "impact") {
              return createImpactSectionEntry({
                user,
                idea,
                section,
                activeItemId,
                scrollToItem,
                isEditing,
              });
            }
            if (util.shouldShowIdeaSection(idea, section)) {
              if (section.type === "assessment") {
                if (!isAssessingSelectedRound || !assessmentState) {
                  return null;
                }
                return createAssessmentSectionEntry({
                  assessment: assessmentState,
                  section,
                  activeSectionId: activeItemId,
                  scrollToSection: scrollToItem,
                  activeFieldId: fieldScroll.activeItemId,
                  scrollToField: fieldScroll.scrollToItem,
                });
              }
              return createCustomSectionEntry({
                user,
                idea,
                section,
                activeItemId,
                scrollToItem,
                isEditing,
              });
            }
            return null;
          }),
          (isEditing || (idea?.attachments?.length ?? 0) > 0) &&
          idea?.ownerIdeaTemplate?.enableAttachments
            ? {
                key: "attachments",
                type: "link" as const,
                content: t("generic.attachments"),
                iconLeft: PaperclipIcon,
                active: activeItemId === "attachments",
                onClick: () => scrollToItem("attachments"),
              }
            : null,
          (!isEditing || (idea?.commentCount ?? 0) >= 1) &&
          !idea?.ownerChallenge?.preventIdeaComments &&
          !loading
            ? {
                key: "comments",
                type: "link" as const,
                content: t("generic.comments"),
                iconLeft: MessagesSquareIcon,
                active: activeItemId === "comments",
                onClick: () => scrollToItem("comments"),
              }
            : null,

          !loading && idea?.ownerChallenge?.questionsEnabled
            ? {
                key: "questions",
                type: "link" as const,
                content: t("generic.questions"),
                iconLeft: MessageCircleIcon,
                active: activeItemId === "questions",
                onClick: () => {
                  setIsShowingQuestions(!isShowingQuestions);
                },
              }
            : null,
        ].filter((item) => Boolean(item))}
        main={main}
        afterContent={
          !main || loading ? null : (
            <>
              {!idea?.ownerChallenge?.ideaAnonymity &&
              (isEditing || canManageChallenge) ? (
                <>
                  <Divider />
                  <div style={{ padding: "0 12px" }}>
                    <IdeaTeam
                      updateIdea={safeUpdateIdea}
                      idea={idea}
                      isSidebar
                      canEdit={canEdit}
                      leaveIdea={safeLeaveIdea}
                    />
                  </div>
                </>
              ) : null}
              {!util.canEditIdea(user, idea) &&
              !idea?.ownerChallenge?.preventCollaboration &&
              !loading ? (
                <>
                  <Divider
                    fitted
                    hidden={
                      !idea?.ownerChallenge?.ideaAnonymity &&
                      (isEditing || canManageChallenge)
                    }
                  />
                  <RequestIdeaTeam idea={idea} updateIdea={safeUpdateIdea} />
                </>
              ) : null}
              {isEditing &&
              (idea?.ownerChallenge?.tagList ?? []).filter(
                (tag) => !tag?.disabledEntities?.includes("ideas"),
              ).length > 0 ? (
                <>
                  <Divider />
                  <div style={{ padding: "0 12px" }}>
                    <H5>{t("ideas.tags.title")}</H5>
                    <IdeaTags updateIdea={safeUpdateIdea} idea={idea} />
                  </div>
                </>
              ) : null}
              {!(isEditing && canEdit) ? (
                <>
                  <Divider />
                  <ReportContentChip forType="idea" forId={idea?._id} />
                </>
              ) : null}
            </>
          )
        }
      />
    </>
  );
};

export default IdeaSidebar;
